import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { APP } from "@/config";
import { Button, Card, Pill } from "@/components/ui-kit";
import MathText from "@/components/MathText";
import api from "@/lib/api";
import { track } from "@/lib/track";
import { startCheckout } from "@/lib/checkout";
import { useAuth } from "@/context/AuthContext";
import LandingFooter from "@/components/LandingFooter";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import { TRUST_CARDS, PRICING, FAQ, FOUNDER } from "@/landingContent";
import {
  Brain, Compass, MessagesSquare, LineChart, Layers, ShieldCheck, Lock,
  Check, ChevronDown, ArrowRight, Sparkles, Clock, Target, Gift,
} from "lucide-react";

const FEATURES = [
  { icon: Compass, title: "Adaptive diagnostic", body: "A short, smart check finds the real gaps — then reorders the path so foundations come first." },
  { icon: Brain, title: "Mastery-first plan", body: "Prerequisite-aware learning. Skills unlock only once the ideas beneath them are solid." },
  { icon: MessagesSquare, title: "Socratic AI tutor", body: "Never hands over the answer. It asks the next good question and diagnoses misconceptions." },
  { icon: Layers, title: "Spaced review", body: "Flashcards and 7-day retention checks keep mastered skills from slipping away." },
  { icon: LineChart, title: "Parent visibility", body: "See gaps closed, time on task, tutor logs, and what's coming next — every week." },
  { icon: ShieldCheck, title: "Calm, accessible", body: "Mobile-first, WCAG-minded, high-contrast and dyslexia-friendly options built in." },
];

function TutorPreview() {
  return (
    <div className="space-y-3" data-testid="preview-tutor">
      <div className="flex justify-start">
        <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-[var(--ns-primary-light)] text-[var(--ns-text)] px-4 py-2.5 text-sm">
          What do you think the <b>first step</b> is for <MathText text={"$\\frac{1}{2}+\\frac{1}{3}$"} />?
        </div>
      </div>
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-[var(--ns-primary)] text-white px-4 py-2.5 text-sm">Add the tops? So 2/5?</div>
      </div>
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-[var(--ns-primary-light)] text-[var(--ns-text)] px-4 py-2.5 text-sm">
          Good thinking — let's test it. Can halves and thirds be added directly, or do the pieces need to be the <b>same size</b> first?
        </div>
      </div>
    </div>
  );
}

function PathPreview() {
  const rows = [
    { name: "Multiply multi-digit numbers", tone: "mastered", label: "Mastered" },
    { name: "Equivalent fractions", tone: "mastered", label: "Mastered" },
    { name: "Add fractions, unlike denominators", tone: "gold", label: "In progress" },
    { name: "Subtract fractions, unlike denominators", tone: "muted", label: "Up next" },
    { name: "Add & subtract mixed numbers", tone: "muted", label: "Locked" },
  ];
  return (
    <div className="space-y-2" data-testid="preview-path">
      {rows.map((r) => (
        <div key={r.name} className="flex items-center justify-between rounded-xl border border-[var(--ns-border)] px-3 py-2.5">
          <span className="text-sm text-[var(--ns-text)]">{r.name}</span>
          <Pill tone={r.tone}>{r.label}</Pill>
        </div>
      ))}
    </div>
  );
}

function ReportPreview() {
  return (
    <div className="space-y-4" data-testid="preview-report">
      <div className="grid grid-cols-3 gap-2 text-center">
        {[["3", "skills mastered"], ["2", "gaps closed"], ["58m", "time on task"]].map(([v, l]) => (
          <div key={l} className="rounded-xl bg-[var(--ns-primary-light)] px-2 py-3">
            <div className="font-head text-2xl font-bold text-[var(--ns-primary)]">{v}</div>
            <div className="text-xs text-[var(--ns-muted)]">{l}</div>
          </div>
        ))}
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-1">Tutor highlights</div>
        <p className="text-sm text-[var(--ns-muted)]">Worked through a common fraction-addition misconception and self-corrected to using common denominators.</p>
      </div>
      <div className="rounded-xl border border-[var(--ns-border)] px-3 py-2.5">
        <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)]">What's coming next</div>
        <p className="text-sm text-[var(--ns-text)] mt-1">Subtracting fractions with unlike denominators, then mixed numbers.</p>
      </div>
    </div>
  );
}

function DeviceFrame({ title, children }) {
  return (
    <div className="rounded-[26px] bg-[var(--ns-text)] p-2 shadow-xl">
      <div className="rounded-[20px] bg-[var(--ns-surface)] overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--ns-border)]">
          <div className="w-2.5 h-2.5 rounded-full bg-[#E05A47]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#E0A93D]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#2A8555]" />
          <span className="ml-2 text-xs font-semibold text-[var(--ns-subtle)]">{title}</span>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const { setUser, user } = useAuth();
  const [tab, setTab] = useState("path");
  const [billing, setBilling] = useState("monthly");

  const choosePlan = (p) => {
    track("cta_click", { cta: "pricing_" + p.id });
    if (p.free) { nav("/auth?mode=register"); return; }
    const lookup = billing === "yearly" ? p.lookupYearly : p.lookupMonthly;
    if (user && user.role === "parent") { startCheckout(lookup, user.id); }
    else { nav("/auth?mode=register"); }
  };

  const startDiagnostic = () => {
    track("cta_click", { cta: "hero_diagnostic" });
    nav("/diagnostic");
  };
  const doDemo = async () => {
    track("demo_started", { source: "landing" });
    try {
      const { data } = await api.post("/auth/demo-login");
      localStorage.setItem("ns_token", data.access_token);
      setUser(data.user);
      nav("/home", { replace: true });
    } catch { toast.error("Demo is unavailable right now. Please try again."); }
  };

  const TABS = [
    { id: "path", label: "Student path", node: <PathPreview /> },
    { id: "tutor", label: "Tutor", node: <TutorPreview /> },
    { id: "report", label: "Parent report", node: <ReportPreview /> },
  ];

  return (
    <div className="band-3-5 min-h-screen bg-[var(--ns-bg)]">
      <header className="max-w-6xl mx-auto px-5 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[var(--ns-primary)] text-white grid place-items-center font-head font-bold">N</div>
          <span className="font-head text-lg font-bold text-[var(--ns-text)]">{APP.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <a href="#pricing" className="hidden sm:inline text-sm font-semibold text-[var(--ns-muted)] hover:text-[var(--ns-primary)] px-3">Pricing</a>
          <Button variant="ghost" data-testid="nav-login-btn" onClick={() => nav("/auth?mode=login")}>Log in</Button>
          <Button data-testid="nav-signup-btn" onClick={() => nav("/auth?mode=register")}>Get started</Button>
        </div>
      </header>

      {/* 1. HERO */}
      <section className="max-w-6xl mx-auto px-5 pt-8 pb-16 grid md:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="text-xs font-semibold uppercase tracking-widest text-[var(--ns-primary)] mb-4">Grades 3–8 · mastery math</div>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[var(--ns-text)] leading-[1.05]">
            Find your child's math gaps — in 5 minutes.
          </h1>
          <p className="mt-5 text-lg text-[var(--ns-muted)] max-w-md">
            {APP.name} pinpoints the real gaps, builds a prerequisite-aware mastery plan, and coaches with a Socratic tutor that never just gives the answer.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button data-testid="hero-start-btn" onClick={startDiagnostic} className="px-7">Find your child's math gaps — free, 5 minutes</Button>
            <Button variant="outline" data-testid="hero-demo-btn" onClick={doDemo}>Try the demo</Button>
          </div>
          <p className="mt-4 text-sm text-[var(--ns-subtle)]">No account needed to start · about 5 minutes</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }}>
          <DeviceFrame title="Socratic tutor"><TutorPreview /></DeviceFrame>
        </motion.div>
      </section>

      {/* 2. HOW IT WORKS */}
      <section className="max-w-6xl mx-auto px-5 pb-4">
        <div className="rounded-3xl bg-[var(--ns-primary)] text-white p-8 sm:p-10">
          <h2 className="font-head text-2xl sm:text-3xl font-bold">How NumberSense works</h2>
          <p className="mt-2 text-[#CDE6D8] max-w-xl">Three simple steps, then a calm daily loop. We reward mastery, not speed.</p>
          <div className="mt-8 grid sm:grid-cols-3 gap-6">
            {[
              { n: "1", t: "Diagnose the gaps", b: "A short adaptive check finds what's really missing — by prerequisite, not just grade." },
              { n: "2", t: "Follow a mastery plan", b: "Skills unlock in the right order. Each day: one lesson, a little practice, quick review." },
              { n: "3", t: "Practice & prove it", b: "Mastery checks confirm understanding; the Socratic tutor helps without giving answers." },
            ].map((s) => (
              <div key={s.n} className="flex gap-3">
                <div className="w-9 h-9 rounded-full bg-white/15 grid place-items-center font-head font-bold shrink-0">{s.n}</div>
                <div>
                  <h3 className="font-head text-lg font-semibold">{s.t}</h3>
                  <p className="mt-1 text-sm text-[#CDE6D8] leading-relaxed">{s.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. SEE IT IN ACTION */}
      <section className="max-w-6xl mx-auto px-5 py-16" data-testid="see-it-in-action">
        <h2 className="font-head text-2xl sm:text-3xl font-bold text-[var(--ns-text)]">See it in action</h2>
        <p className="mt-2 text-[var(--ns-muted)]">A real look at Maya's experience — Grade 5.</p>
        <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Product preview">
          {TABS.map((t) => (
            <button key={t.id} role="tab" aria-selected={tab === t.id} data-testid={`preview-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ns-primary)] ${tab === t.id ? "bg-[var(--ns-primary)] text-white" : "bg-[var(--ns-surface)] border border-[var(--ns-border)] text-[var(--ns-muted)]"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="mt-6 max-w-xl">
          <DeviceFrame title={TABS.find((t) => t.id === tab).label + " · Maya, Grade 5"}>
            {TABS.find((t) => t.id === tab).node}
          </DeviceFrame>
        </div>
      </section>

      {/* 4. BUILT FOR THE DAILY LOOP */}
      <section className="max-w-6xl mx-auto px-5 pb-16">
        <h2 className="font-head text-2xl sm:text-3xl font-bold text-[var(--ns-text)] mb-8">Built for the daily loop — and parent confidence.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Card className="p-6 h-full">
                <div className="w-11 h-11 rounded-xl bg-[var(--ns-primary-light)] text-[var(--ns-primary)] grid place-items-center mb-4"><f.icon size={22} /></div>
                <h3 className="font-head text-lg font-semibold text-[var(--ns-text)]">{f.title}</h3>
                <p className="mt-2 text-sm text-[var(--ns-muted)] leading-relaxed">{f.body}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 5. SAFE FOR KIDS */}
      <section className="max-w-6xl mx-auto px-5 pb-16" data-testid="safe-for-kids">
        <h2 className="font-head text-2xl sm:text-3xl font-bold text-[var(--ns-text)]">Safe for kids, built for parents</h2>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TRUST_CARDS.map((c) => (
            <Card key={c.title} className="p-5 h-full">
              <div className="w-11 h-11 rounded-xl bg-[var(--ns-primary-light)] text-[var(--ns-primary)] grid place-items-center mb-3"><c.icon size={22} /></div>
              <h3 className="font-head text-base font-semibold text-[var(--ns-text)]">{c.title}</h3>
              <p className="mt-2 text-sm text-[var(--ns-muted)] leading-relaxed">{c.body}</p>
            </Card>
          ))}
        </div>
        <Link to="/privacy" data-testid="privacy-link" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-[var(--ns-primary)] hover:underline">
          Read our privacy policy <ArrowRight size={15} />
        </Link>
      </section>

      {/* 7. ABOUT FOUNDER */}
      <section className="max-w-6xl mx-auto px-5 pb-16" data-testid="founder">
        <Card className="p-6 sm:p-8 grid sm:grid-cols-[auto_1fr] gap-6 items-center">
          <div className="w-28 h-28 rounded-2xl bg-[var(--ns-primary-light)] grid place-items-center text-[var(--ns-primary)]" aria-label="Founder photo placeholder">
            <Sparkles size={34} />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)]">Why we built NumberSense</div>
            <h3 className="font-head text-xl font-bold text-[var(--ns-text)] mt-1">{FOUNDER.name}</h3>
            <div className="text-sm text-[var(--ns-primary)] font-semibold">{FOUNDER.credentials}</div>
            <p className="mt-3 text-[var(--ns-muted)] leading-relaxed">{FOUNDER.story}</p>
          </div>
        </Card>
      </section>

      {/* 8. PRICING */}
      <section id="pricing" className="max-w-6xl mx-auto px-5 py-16 scroll-mt-20" data-testid="pricing"
        onMouseEnter={() => track("pricing_viewed", {})}>
        <h2 className="font-head text-2xl sm:text-3xl font-bold text-[var(--ns-text)]">Simple, honest pricing</h2>
        <p className="mt-2 text-sm text-[var(--ns-muted)]">{PRICING.note}</p>

        <div className="mt-6 flex rounded-full bg-[var(--ns-surface-subtle)] p-1 w-fit" data-testid="billing-toggle">
          {[["monthly", "Monthly"], ["yearly", "Annual · save ~17%"]].map(([v, l]) => (
            <button key={v} data-testid={`landing-billing-${v}`} onClick={() => setBilling(v)}
              className="rounded-full px-4 py-2 text-sm font-semibold transition-colors"
              style={{ background: billing === v ? "var(--ns-surface)" : "transparent", color: billing === v ? "var(--ns-primary)" : "var(--ns-muted)" }}>
              {l}
            </button>
          ))}
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {PRICING.plans.map((p) => {
            const amount = billing === "yearly" ? p.yearly : p.monthly;
            const unit = p.free ? "" : (billing === "yearly" ? "/yr" : "/mo");
            return (
              <Card key={p.id} data-testid={`plan-${p.id}`} className={`p-6 h-full flex flex-col ${p.highlight ? "ring-2 ring-[var(--ns-primary)]" : ""}`}>
                {p.highlight && <Pill tone="primary" className="self-start mb-2">Most popular</Pill>}
                <h3 className="font-head text-lg font-bold text-[var(--ns-text)]">{p.name}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-head text-3xl font-bold text-[var(--ns-text)]">{p.free ? "Free" : `$${amount}`}</span>
                  <span className="text-sm text-[var(--ns-subtle)]">{unit}</span>
                </div>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[var(--ns-muted)]"><Check size={16} className="text-[var(--ns-primary)] mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Button className="mt-5 w-full" data-testid={`plan-cta-${p.id}`} onClick={() => choosePlan(p)}>{p.cta}</Button>
              </Card>
            );
          })}
        </div>
        <div className="mt-5 flex flex-wrap gap-4 text-sm text-[var(--ns-muted)]">
          {PRICING.cancelAnytime && <span className="inline-flex items-center gap-1"><Check size={15} className="text-[var(--ns-primary)]" /> Cancel anytime</span>}
          {PRICING.trialNoCard && <span className="inline-flex items-center gap-1"><Check size={15} className="text-[var(--ns-primary)]" /> No credit card required for the trial</span>}
        </div>
      </section>

      {/* 9. FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 py-16 scroll-mt-20" data-testid="faq">
        <h2 className="font-head text-2xl sm:text-3xl font-bold text-[var(--ns-text)] mb-6">Frequently asked questions</h2>
        <div className="space-y-3">
          {FAQ.map((item, i) => <FaqItem key={i} item={item} index={i} />)}
        </div>
      </section>

      {/* 9b. FREE RESOURCES — lead magnet */}
      <section id="free-resources" className="max-w-4xl mx-auto px-5 py-10 scroll-mt-20" data-testid="free-resources">
        <Card className="p-7 sm:p-9 grid md:grid-cols-[1.3fr_1fr] gap-8 items-center">
          <div>
            <div className="w-12 h-12 rounded-xl grid place-items-center mb-3" style={{ background: "var(--ns-primary-light)", color: "var(--ns-primary)" }}>
              <Gift size={24} />
            </div>
            <h2 className="font-head text-2xl font-bold text-[var(--ns-text)]">Free Math Readiness Checklist</h2>
            <p className="mt-2 text-[var(--ns-muted)]">
              Seven quick checks to spot gaps before they snowball — plus a short, practical 5-day "Math at Home" series. No spam, unsubscribe anytime.
            </p>
            <ul className="mt-4 space-y-1.5">
              {["Grade-aware readiness checklist", "5 bite-sized at-home tips", "Written by educators, not marketers"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-[var(--ns-muted)]"><Check size={15} className="text-[var(--ns-primary)]" /> {t}</li>
              ))}
            </ul>
          </div>
          <div onMouseEnter={() => track("lead_form_viewed", { source: "landing_section" })}>
            <LeadCaptureForm source="landing_section" magnet="math_readiness_checklist" compact />
            <p className="mt-2 text-xs text-[var(--ns-subtle)]">We'll email a confirmation link first — double opt-in, your choice.</p>
          </div>
        </Card>
      </section>

      {/* 10. FINAL CTA */}
      <section className="max-w-6xl mx-auto px-5 pb-20">
        <div className="rounded-3xl bg-[var(--ns-primary)] text-white p-10 text-center">
          <h2 className="font-head text-2xl sm:text-3xl font-bold">Find your child's math gaps in 5 minutes</h2>
          <p className="mt-2 text-[#CDE6D8]">Free to start. No account needed to see the first results.</p>
          <div className="mt-6 flex justify-center gap-3 flex-wrap">
            <Button variant="accent" data-testid="final-cta-btn" onClick={startDiagnostic}>Start the free diagnostic</Button>
            <Button variant="outline" className="!border-white/40 !text-white hover:!bg-white/10" onClick={doDemo}>Try the demo</Button>
          </div>
        </div>
      </section>

      <LandingFooter />
      <ExitIntentPopup />
    </div>
  );
}

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="overflow-hidden">
      <button data-testid={`faq-q-${index}`} aria-expanded={open} aria-controls={`faq-a-${index}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ns-primary)]">
        <span className="font-semibold text-[var(--ns-text)]">{item.q}</span>
        <ChevronDown size={18} className={`text-[var(--ns-subtle)] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div id={`faq-a-${index}`} role="region" className="px-5 pb-4 text-sm text-[var(--ns-muted)] leading-relaxed">{item.a}</div>
      )}
    </Card>
  );
}
